import {
  makeFinalFormValidator,
  OverlayAnnotations,
  TypeFromObjectSchema,
} from '@readcloud/common';
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@readcloud/component-library';
import { HtmlAnnotation } from '@readcloud/data';
import React from 'react';
import { Field, Form, FormRenderProps } from 'react-final-form';
import FroalaEditor from 'react-froala-wysiwyg';
import * as yup from 'yup';
import { Forms } from '../..';
import './bootstrapFroala';
import { froalaConfig } from './config';
const schema = yup
  .object({
    html: yup.string(),
    variant: yup.string().required().label('Mode'),
    publisherOptions: Forms.commonSchemas.publisherOptions,
  })
  .shape(Forms.commonSchemas.commonFields);

type SchemaValues = TypeFromObjectSchema<typeof schema>;

const validate = makeFinalFormValidator(schema);

function HTMLAnnotationEditorForm(
  props: FormRenderProps<SchemaValues> & {
    autoSubmit: boolean;
    onCancel?: () => void;
    submitLabel?: string;
    showPublisherOptions: boolean;
  }
) {
  const state = props.form.getState();

  return (
    <form className="rc-flex rc-flex-col" onSubmit={props.handleSubmit}>
      <Forms.AutoSubmit enabled={props.autoSubmit} />
      <div className="rc-w-full froala-editor-container">
        <div className="rc-prose">
          <FroalaEditor
            config={froalaConfig}
            model={state.values.html}
            onModelChange={(val: any) => props.form.change('html', val)}
          />
        </div>
      </div>
      <div className="rc-p-2" />
      <Field name="variant" type="radio" label="Mode">
        {(props) => (
          <ToggleButtonGroup
            size="sm"
            type="single"
            name="variant"
            defaultValue={state.initialValues.variant}
            ensureValue
            onValueChange={(val) => {
              props.input.onChange(val);
            }}
          >
            {OverlayAnnotations.mediaVariants.map((variant, i) => (
              <ToggleButton
                {...props.input}
                variant="light"
                id={`variant-radio-${i}`}
                name="variant"
                value={variant}
                type={props.input.type as 'button'}
              >
                {variant}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Field>
      <div className="rc-py-2" />
      <div className="rc-flex rc-flex-col">
        <Forms.commonElements.CommonFields
          showPublisherOptions={props.showPublisherOptions}
        />
      </div>

      {!props.autoSubmit && (
        <div className="rc-flex rc-justify-end rc-items-center rc-w-full">
          {props.onCancel ? (
            <>
              <Button variant="light" onClick={props.onCancel}>
                Cancel
              </Button>{' '}
              <div className="rc-p-2" />
            </>
          ) : null}

          <Button
            disabled={!state.valid || state.submitting}
            submit
            variant="primary"
          >
            {props.submitLabel || 'Save'}
          </Button>
        </div>
      )}
    </form>
  );
}
export function HTMLAnnotationEditor(props: {
  annotation: HtmlAnnotation;
  onCancel?: () => void;
  onChange: (annotation: HtmlAnnotation) => void;
  submitLabel?: string;
  autoSubmit?: boolean;
  showPublisherOptions: boolean;
}) {
  const handleSubmit = React.useCallback(
    (values: TypeFromObjectSchema<typeof schema>) => {
      props.onChange(
        Forms.commonElements.formValuesToAnnotation(props.annotation, values)
      );
    },
    [props.onChange, props.annotation]
  );
  return (
    <Form
      validate={validate}
      initialValues={
        Forms.commonElements.annotationToFormValues(props.annotation).data
      }
      onSubmit={handleSubmit}
      //the when the initial form values changed becuase of the form auto-submitting on any onChange, it would reinitialise and cause another onChange event to trigger - causing an infinite loop.
      //the only reason this wasn't crashing the app is because the autosubmit is on a 300ms debounce.
      //these 2 props seem to be required to stop that from happening.
      keepDirtyOnReinitialize
      //The initalValuesEqual prop is meant to take a deepEquals function that it uses on the previous/current values. Since we don't use the same form for different annotations
      //(we dont swap another anno's values into the current form at any point), I think it's safe to assume that the previous values should always be the same as the current values here,
      // and we can save on some computation.
      initialValuesEqual={() => true}
      render={(formProps) => (
        <HTMLAnnotationEditorForm
          {...formProps}
          showPublisherOptions={props.showPublisherOptions}
          onCancel={props.onCancel}
          submitLabel={props.submitLabel}
          autoSubmit={!!props.autoSubmit}
        />
      )}
    ></Form>
  );
}
